import { FeedbackListProps } from '@/types/feedback';
import { motion } from 'framer-motion';
import FeedbackCard from './FeedbackCard';
import { AddFeedbackButton } from './AddFeedbackButton';
import { useRef, WheelEvent, MouseEvent, useState, useEffect } from 'react';

interface ExtendedFeedbackListProps extends FeedbackListProps {
  onAddFeedback?: () => void;
}

export const FeedbackList: React.FC<ExtendedFeedbackListProps> = ({
  feedbacks,
  onAddFeedback,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollDirection, setScrollDirection] = useState<'left' | 'right'>(
    'right'
  );
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const autoScrollRef = useRef<number | null>(null);

  // Auto scroll function
  const autoScroll = () => {
    if (scrollContainerRef.current && isAutoScrolling) {
      const container = scrollContainerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;

      // Increment scroll position based on direction
      if (scrollDirection === 'right') {
        container.scrollLeft += 1; // Scrolling right
        if (container.scrollLeft >= maxScroll) {
          setScrollDirection('left'); // Change direction when reaching right end
        }
      } else {
        container.scrollLeft -= 1; // Scrolling left
        if (container.scrollLeft <= 0) {
          setScrollDirection('right'); // Change direction when reaching left end
        }
      }

      autoScrollRef.current = requestAnimationFrame(autoScroll);
    }
  };

  // Initialize and cleanup auto scroll
  useEffect(() => {
    if (isAutoScrolling) {
      autoScrollRef.current = requestAnimationFrame(autoScroll);
    }
    return () => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
    };
  }, [isAutoScrolling]);

  // Handle mouse wheel scrolling
  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current) {
      e.preventDefault();
      scrollContainerRef.current.scrollLeft += e.deltaY;
      setIsAutoScrolling(false);
      setScrollDirection(e.deltaY > 0 ? 'right' : 'left');
    }
  };

  // Handle mouse down
  const handleMouseDown = (e: MouseEvent) => {
    setIsDragging(true);
    setIsAutoScrolling(false); // Pause auto-scroll on user interaction
    if (scrollContainerRef.current) {
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    }
  };
  const handleMouseLeave = () => {
    handleMouseLeaveUp();
    handleMouseExit();
  };

  // Handle mouse leave/up
  const handleMouseLeaveUp = () => {
    setIsDragging(false);
    // Resume auto-scroll after a delay
    setTimeout(() => setIsAutoScrolling(true), 5000);
  };

  // Handle mouse move
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    if (scrollContainerRef.current) {
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - startX) * 2;
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
      // Update direction based on drag movement
      setScrollDirection(walk < 0 ? 'right' : 'left');
    }
  };

  // Handle hover
  const handleMouseEnter = () => {
    setIsAutoScrolling(false); // Pause on hover
  };

  const handleMouseExit = () => {
    // Resume auto-scroll after a delay
    setTimeout(() => setIsAutoScrolling(true), 2000);
  };

  // Clean up event listeners
  useEffect(() => {
    const cleanup = () => {
      setIsDragging(false);
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
    };
    window.addEventListener('mouseup', cleanup);
    return () => window.removeEventListener('mouseup', cleanup);
  }, []);

  return (
    <div className="relative w-full py-4 md:py-8">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_500px_at_50%_200px,rgba(147,51,234,0.1),transparent)] dark:bg-[radial-gradient(circle_500px_at_50%_200px,rgba(147,51,234,0.05),transparent)]" />
      </div>

      {/* Add Feedback Button - Top Right Corner */}
      {onAddFeedback && (
        <div className="absolute -top-2 right-4 md:-top-4 md:right-8 z-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="transform scale-50" // Much smaller - 50% of original size
          >
            <AddFeedbackButton onClick={onAddFeedback} />
          </motion.div>
        </div>
      )}

      {/* Main Content Container */}
      <div className="relative w-full max-w-7xl mx-auto">
        {/* Scroll Container */}
        <div
          ref={scrollContainerRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onTouchStart={() => setIsAutoScrolling(false)}
          onTouchEnd={() => setTimeout(() => setIsAutoScrolling(true), 5000)}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseLeaveUp}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          className={`overflow-x-auto scrollbar-hide cursor-grab select-none
              ${isDragging ? 'cursor-grabbing' : ''}`}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex pb-2 md:pb-4 min-w-full"
          >
            {/* Double the content for seamless infinite scroll */}
            <div className="flex gap-6 px-8 snap-x snap-mandatory">
              {feedbacks.map((feedback, index) => (
                <div
                  key={`${feedback.id}-${index}`}
                  className="snap-center shrink-0 first:pl-2 last:pr-2 md:first:pl-4 md:last:pr-4 
                         w-[280px] sm:w-[320px]"
                >
                  <FeedbackCard feedback={feedback} index={index} />
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Gradient Fade Effects */}
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-purple-50 dark:from-gray-900 to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-purple-50 dark:from-gray-900 to-transparent pointer-events-none" />
      </div>
    </div>
  );
};

export default FeedbackList;
